import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Grid2, InputAdornment, Typography } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import { addDays, isPast, isSameDay } from "date-fns";
import format from "date-fns/format";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useDateFnsLocale } from "../../../../utils/date-locale";
import useWorkerSchedulerData from "../../controllers/use-worker-scheduler-data";
import { setSearchTerm } from "../../store/worker-scheduler-slice";
const BORDER_COLOR = "#E5E5E5";

// Constants for layout
const SIDEBAR_WIDTH = "210px"; // Width for the sidebar (if any)
const MAIN_CONTENT_FLEX = 1; // Remaining space will be divided equally

export const CalendarHeader = ({
                                 isPrinting = false,
  datesWidthPercentage = 40,
                                 showTimeText = false,
  title = <></>,
  width = "100%",
  borderLess = false,
                               }) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const dateLocale = useDateFnsLocale();

	const { currentDate, startingDay, searchedTerm } = useWorkerSchedulerData();

	return (
		<Grid2
			container
			width={"100%"}
      minWidth={SIDEBAR_WIDTH + 164 * 7} // 7 days, each 164px wide
			sx={{
				flexGrow: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
			}}
		>
			{/* Day headers */}
			<Grid2
				container
				width={width}
				sx={{
					position: "sticky",
					top: 0,
					height: isPrinting ? "36px" : "64px",
					minHeight: isPrinting ? "36px" : "64px",
					backgroundColor: isPrinting ? "#fafafa" : "rgba(255,255,255)",
          borderTop: borderLess ? "none" : `1px solid ${BORDER_COLOR}`,
          borderLeft: borderLess ? "none" : `1px solid ${BORDER_COLOR}`,
          borderRight: borderLess ? "none" : `1px solid ${BORDER_COLOR}`,
					flexWrap: "nowrap",
				}}
			>
				{/* Sidebar space reservation */}
				<Box
					sx={{
						width: isPrinting ? `${100-datesWidthPercentage}%` : SIDEBAR_WIDTH,
            minWidth: SIDEBAR_WIDTH,
            borderBottom: borderLess ? "none" : `1px solid ${BORDER_COLOR}`,
						height: "100%",
						display: "flex",
						justifyContent: "flex-start",
						alignItems: "center",
            paddingLeft: "16px",
					}}
				>
          {!isPrinting && (
            <InputBase
              startAdornment={
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                searchedTerm.length > 0 && (
                  <InputAdornment
                    position='end'
                    onClick={() => dispatch(setSearchTerm(""))}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <CloseIcon />
                  </InputAdornment>
                )
              }
              placeholder={t("common_search")}
              value={searchedTerm}
              onChange={(e) => dispatch(setSearchTerm(e.target.value))}
              sx={{
                width: "100%",
                height: "100%",
                padding: "0 16px",
                border: "none",
                outline: "none",
                transition: "width 0.3s ease",
              }}
            />
          )}
          {isPrinting && title}
				</Box>
				{/* Days container */}
				<Box
					sx={{
						display: "flex",
						flex: MAIN_CONTENT_FLEX,
						height: "100%",
						zIndex: 1000,
            flexDirection: "row",
            width: isPrinting ? "60%" : "100%",
            overflowX: "hidden",
					}}
				>
					{currentDate &&
						Array.from({ length: 7 }).map((_, index) => {
							const day = addDays(startingDay, index);
							return (
								<React.Fragment key={index}>
									<Box
										sx={{
											display: "flex",
											flex: 1,
											flexDirection: "row",
											justifyContent: isPrinting ? "center" : "flex-start",
											alignItems: "center",
											textAlign: "center",
											height: "100%",
											position: "relative",
											paddingLeft: isPrinting ? 0 : "0.8rem",
											borderLeft: borderLess ? "none" : `1px solid ${BORDER_COLOR}`,
											borderBottom: borderLess ? "none" : `1px solid ${BORDER_COLOR}`,
											backgroundColor: !isPrinting ? isSameDay(day, new Date())
												? "rgba(47, 77, 139, 0.04)"
												: isPast(new Date(day))
												? "rgba(229, 229, 229, 0.2)"
												: "" : "",
											width: isPrinting ? `${datesWidthPercentage/7}%` : `${100 / 7}%`,
										}}
									>
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												justifyContent: "center",
												alignItems: borderLess ? "center" : "flex-start",
                        width: "100%",
											}}
										>
                      <Typography
                        sx={{
                          fontSize: isPrinting ? "12px" : "16px",
                          lineHeight: isPrinting ? "16px" : "28px",
                          fontWeight: 400,
                          fontFamily: "Roboto",
                          color: !isPrinting ? isSameDay(day, new Date())
                            ? "#2F4D8B"
                            : "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.6)",
                          marginRight: isPrinting ? 0 : "0.5rem",
                        }}
                      >
                        {format(day, "EEE", { locale: dateLocale })}
                      </Typography>
                      {!borderLess &&
                        <Typography
                          sx={{
                            fontSize: isPrinting ? "13px" : "24px",
                            lineHeight: isPrinting ? "16px" : "32px",
                            fontWeight: 400,
                            fontFamily: "Roboto",
                            color: !isPrinting ? isSameDay(day, new Date())
                              ? "#2F4D8B"
                              : "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.6)",
                          }}
                        >
                          {format(day, "d")}
                        </Typography>}
										</Box>
									</Box>
								</React.Fragment>
							);
						})}
				</Box>
			</Grid2>
		</Grid2>
	);
};
